import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiArrowRight, FiLogOut, FiShield, FiX } from 'react-icons/fi';
import { installAppBackHistory } from '../utils/appBackHistory';
import './app-back-navigation.css';

const BackContext = createContext(null);
const DASHBOARD = /^\/(student-dashboard|(?:guest|advisor|supervisor|supporter|admin|parent|question-designer)\/dashboard|advisor\/send-plan\/)/;

export function useAppBackAction(action, enabled = true, kind = 'page', priority = 0) {
  const registry = useContext(BackContext);
  const latest = useRef(action);
  latest.current = action;
  useLayoutEffect(() => {
    if (!registry || !enabled) return undefined;
    const entry = { action: () => latest.current(), kind, priority };
    registry.add(entry);
    return () => registry.delete(entry);
  }, [registry, enabled, kind, priority]);
}

export function useAppBackGuard(message, enabled = true, blocked = false) {
  useAppBackAction(() => ({ message, blocked }), enabled, 'guard');
}

export function useDashboardBack({ page, setPage, menuOpen, setMenuOpen, home = 'home' }) {
  useAppBackAction(() => { setMenuOpen(false); }, menuOpen, 'menu');
  useAppBackAction(() => {
    const key = typeof page === 'object' ? page?.page : page;
    if (key === home) return false;
    setPage(home);
    return true;
  }, true, 'fallback');
}

function visible(element) {
  if (!element?.isConnected || element.closest('[hidden],[aria-hidden="true"]')) return false;
  let parent = element;
  while (parent instanceof Element) {
    const style = window.getComputedStyle(parent);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    parent = parent.parentElement;
  }
  return element.getClientRects().length > 0;
}

function topDialog() {
  const dialogs = [...document.querySelectorAll('[role="dialog"],dialog[open],[class*="modal-overlay"],[class*="modal-backdrop"],.exam-submit-overlay')].filter(visible);
  const zIndex = (element) => {
    let z = 0;
    for (let node = element; node instanceof Element; node = node.parentElement) {
      z = Math.max(z, Number.parseInt(window.getComputedStyle(node).zIndex, 10) || 0);
    }
    return z;
  };
  dialogs.sort((a, b) => zIndex(a) - zIndex(b));
  return dialogs[dialogs.length - 1];
}

function closeDialog(dialog) {
  // Only invoke existing close/cancel controls. Never synthesize Escape globally:
  // several stacked dialogs listen for it and would close together.
  const buttons = [...dialog.querySelectorAll('button')].filter((button) => button.closest('[role="dialog"]') === dialog || !dialog.matches('[role="dialog"]'));
  const close = buttons.find((button) => /^(بستن|انصراف|لغو)(\s|$)/.test((button.getAttribute('aria-label') || button.title || button.textContent).trim()))
    || dialog.querySelector('.pension-modal-head>button,.pension-program-picker header>button,.chat-student-profile-close,.modal-close,.close-modal');
  if (close && !close.disabled && visible(close)) close.click();
  // No close control or disabled while saving: consume Back, keep the dialog safe.
}

function pageBackButton() {
  return [...document.querySelectorAll('button')]
    .find((button) => !button.disabled && !button.closest('nav,aside,[role="dialog"]') && visible(button)
      && (button.matches('[data-app-back],.history-back-icon-btn,.chat-back,.report-back-btn,.back-btn,.back-button') || [button.getAttribute('aria-label'), button.title, button.textContent].some((label) => /^بازگشت(?:\s|$)/.test((label || '').trim()))));
}

export default function AppBackNavigation({ children, pathname = window.location.pathname }) {
  const registry = useRef(new Set()).current;
  const historyGuard = useRef(null);
  const historyOwner = useRef(null);
  const [confirmation, setConfirmation] = useState(null);
  const confirmationRef = useRef(null);
  confirmationRef.current = confirmation;
  const active = DASHBOARD.test(pathname);
  const entries = useCallback((kind) => [...registry].filter((item) => item.kind === kind).reverse().sort((a, b) => b.priority - a.priority), [registry]);
  const navigateBack = useCallback(() => {
    const page = entries('page')[0];
    if (page && page.action() !== false) return;
    const button = pageBackButton();
    if (button) { button.click(); return; }
    const fallback = entries('fallback')[0];
    if (fallback && fallback.action() !== false) return;
    setConfirmation({ kind: 'exit', title: 'از برنامه خارج می‌شوید؟', message: 'از حساب کاربری خارج نمی‌شوید و می‌توانید دوباره به برنامه برگردید.' });
  }, [entries]);
  const backRef = useRef(null);
  backRef.current = () => {
    if (confirmationRef.current) { setConfirmation(null); return; }
    const dialog = topDialog();
    if (dialog) { closeDialog(dialog); return; }
    const menu = entries('menu')[0];
    if (menu) { menu.action(); return; }
    const guard = entries('guard')[0]?.action();
    if (guard) {
      setConfirmation({ kind: guard.blocked ? 'blocked' : 'leave', title: guard.blocked ? 'لطفاً کمی صبر کنید' : 'از این بخش برمی‌گردید؟', message: guard.message });
      return;
    }
    navigateBack();
  };
  useLayoutEffect(() => {
    setConfirmation(null);
    if (!active) return undefined;
    let controller;
    try {
      controller = installAppBackHistory({ owner: historyOwner.current, onBack: () => backRef.current(), onUnavailable: () => setConfirmation({ kind: 'unavailable', title: 'بستن برنامه', message: 'مرورگر اجازه بستن خودکار این صفحه را نمی‌دهد. برای خروج، از دکمه خانه یا فهرست برنامه‌های گوشی، یا بستن تب مرورگر استفاده کنید.' }) });
    } catch (error) {
      // Restricted browser history must never make the dashboard unusable.
      console.warn('App Back navigation is unavailable in this browser', error);
      return undefined;
    }
    historyOwner.current = controller.owner;
    historyGuard.current = controller;
    return () => { controller.destroy(); historyGuard.current = null; };
  }, [active, pathname]);
  const cancelRef = useRef(null);
  useEffect(() => {
    if (!confirmation) return undefined;
    const previous = document.activeElement;
    cancelRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); setConfirmation(null); }
      if (event.key === 'Tab') {
        const buttons = [...document.querySelectorAll('.app-back-dialog button')];
        const last = buttons[buttons.length - 1];
        if (buttons.length && ((event.shiftKey && document.activeElement === buttons[0]) || (!event.shiftKey && document.activeElement === last))) {
          event.preventDefault(); (event.shiftKey ? last : buttons[0]).focus();
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('keydown', onKey, true); if (previous?.isConnected) previous.focus(); };
  }, [confirmation]);
  const confirm = () => {
    const kind = confirmation?.kind;
    setConfirmation(null);
    if (kind === 'exit') historyGuard.current?.exit();
    if (kind === 'leave') navigateBack();
  };
  return <BackContext.Provider value={registry}>{children}{confirmation && createPortal(
    <div className="app-back-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setConfirmation(null)}>
      <section className="app-back-dialog" role="dialog" aria-modal="true" aria-labelledby="app-back-title" aria-describedby="app-back-description" dir="rtl">
        <button type="button" className="app-back-close" aria-label="بستن" onClick={() => setConfirmation(null)}><FiX /></button>
        <span className="app-back-symbol">{confirmation.kind === 'exit' ? <FiLogOut /> : <FiShield />}</span>
        <h2 id="app-back-title">{confirmation.title}</h2><p id="app-back-description">{confirmation.message}</p>
        <div className="app-back-actions"><button type="button" ref={cancelRef} onClick={() => setConfirmation(null)}><FiArrowRight />{['blocked', 'unavailable'].includes(confirmation.kind) ? 'متوجه شدم' : 'ماندن در برنامه'}</button>{['exit', 'leave'].includes(confirmation.kind) && <button type="button" className="confirm" onClick={confirm}>{confirmation.kind === 'exit' ? 'خروج از برنامه' : 'بازگشت'}</button>}</div>
      </section>
    </div>, document.body)}</BackContext.Provider>;
}
